import { connect } from 'react-redux';

import { CameraType, MediaType } from '../../../../data/media';
import { createPreviewVideo, videoPlaybackError, clearPlaybackError } from '../../../../data/media/actions';
import { createEventUserVideo, LOCAL_ONLY_EVENT_TYPES } from '../../../../data/events/actions';

import EventMedia from './EventMedia';
import { setCamera } from './actions';
import { EVENT_MEDIA_STATUS, EVENT_MEDIA_REQUEST_TYPE, EVENT_MEDIA_CAMERA_TYPE } from './eventMediaConstants';
import cameraTypeOverride from './cameraTypeOverride';

// returns List<Map<Media>>
const getMediaForEvent = (state, ravenUuid, event) => {
    return event.get('mediaIds').toSeq()
        .map(mediaId => state.data.media.mediaMap.getIn([ravenUuid, mediaId]))
        .filter(m => m); // filter out undefined/null
}

const getUrl = (media) => {
    return media ? (media.get('_publicUrl') || media.getIn(['signedUrl', 'url'])) : '';
};

const getSourceMedia = (mediaList, cameraType) => {
    return mediaList.find(m => m && m.get('cameraType') === cameraType && m.get('type') === MediaType.MEDIA_SHORT);
}

const getEventVideoAvailable = (event, cameraType) => {

    const eventVideoAvailable = event.get('videoAvailable');
    if (!eventVideoAvailable) {
        console.warn("EventMedia getEventVideoAvailable with event missing videoAvailable property");
        return false;
    }
    //console.log("eventVideoAvailable: " + JSON.stringify(eventVideoAvailable));
    if (cameraType === CameraType.CAMERA_ROAD) {
        return eventVideoAvailable.get("road");
    }
    return eventVideoAvailable.get("cabin");
}

export const getPreviewVideoMedia = (mediaList, cameraType, clusterItemIndex = undefined) => {
    // If we have a preview video for the given camera type, or any streaming archive, return that.
    // TODO - we need to handle any number of media types, not hardcoded types, but that's a bigger issue
    const previewVideoMedia = mediaList.filter(m => { 
            return m && (
                (m.get('cameraType') === cameraType && m.get('type') === MediaType.MEDIA_PREVIEW_VIDEO) ||
                (m.get('type') === MediaType.MEDIA_STREAMING_ARCHIVE)
            );
        })
        .sort((m1, m2) => m2.get('statusTime') - m1.get('statusTime'));

    if (clusterItemIndex !== undefined) {
        if (clusterItemIndex < previewVideoMedia.size) {
            return previewVideoMedia.toIndexedSeq().get(clusterItemIndex);
        } else {
            console.warn("EventMedia getPreviewVideoMedia clusterItemIndex out of bounds");
            return previewVideoMedia.last();
        }
    }
    return previewVideoMedia.first();
}

const getPreviewImageMedia = (mediaList, cameraType) => {
    return mediaList.find(m => m && m.get('cameraType') === cameraType && m.get('type') === MediaType.MEDIA_PREVIEW);
}

const getEvent = (state, props) => {
    return state.data.events.eventMap.getIn([props.selectedRavenUuid, props.selectedEventId]);
};

const getEventType = (event) => {
    return event ? event.get('type') : null;
};

export const expiryCheck = (
    statusTime,
    expirySeconds,
    returnStatus = EVENT_MEDIA_STATUS.CREATE_PREVIEW_VIDEO_REQUEST_SENT,
    expiredStatus = EVENT_MEDIA_STATUS.ERROR_REQUEST_TIMEOUT
) => {
    return statusTime
        && typeof statusTime.getTime === 'function'
        && expirySeconds
        && (statusTime.getTime() + (expirySeconds * 1000) > Date.now()) ? returnStatus : expiredStatus;
};

const getCameraTypeForEventUserMediaRequest = (eventUserMediaRequest) => {

    if (!eventUserMediaRequest) return null;

    const originalMqttRequest =  eventUserMediaRequest.get('request');

    if (!originalMqttRequest) return null;

    const originalMqttRequestCameraType = originalMqttRequest.get('cameraType');

    return originalMqttRequestCameraType ? originalMqttRequestCameraType : null;
}

// getUserMediaRequestStatus will return null if error for other cameraType
const getUserMediaRequestStatus = (eventUserMediaRequest, cameraType) => {
    const statusEventType = eventUserMediaRequest.get('statusEventType');
    let errorFound = false;
    let statusForCameraType = "";
    switch (statusEventType) {
        case "EVENT_MEDIA_INITIATED":
            statusForCameraType = EVENT_MEDIA_STATUS.MEDIA_INITIATED;
            break;
        case "EVENT_MEDIA_CREATED":
            statusForCameraType = EVENT_MEDIA_STATUS.MEDIA_CREATED;
            break;
        case "EVENT_MEDIA_UPLOAD_INITIATED":
            statusForCameraType = EVENT_MEDIA_STATUS.MEDIA_UPLOAD_INITIATED;
            break;
        case "EVENT_MEDIA_UPLOAD_FAILED":
            statusForCameraType = EVENT_MEDIA_STATUS.ERROR_UPLOAD_FAILED;
            errorFound = true;
            break;
        case "EVENT_MEDIA_SHORT_FAILED":
            statusForCameraType = EVENT_MEDIA_STATUS.ERROR_SHORT_FAILED;
            errorFound = true;
            break;
        case "EVENT_MEDIA_RECORDING_FAIL":
            statusForCameraType = EVENT_MEDIA_STATUS.ERROR_RECORDING_FAIL;
            errorFound = true;
            break;
        case "EVENT_MEDIA_DOWNLOAD_FAILED":
            statusForCameraType = EVENT_MEDIA_STATUS.ERROR_DOWNLOAD_FAILED;
            errorFound = true;
            break;
        case "EVENT_MEDIA_FAILED":
            statusForCameraType = EVENT_MEDIA_STATUS.ERROR_MEDIA_FAILED;
            errorFound = true;
            break;
        case LOCAL_ONLY_EVENT_TYPES.EVENT_MEDIA_INITIATED_WEBAPP_TIMEOUT:
        case LOCAL_ONLY_EVENT_TYPES.EVENT_MEDIA_STATUS_UPDATE_WEBAPP_TIMEOUT:
            statusForCameraType = EVENT_MEDIA_STATUS.ERROR_REQUEST_TIMEOUT;
            errorFound = true;
            break;
        case LOCAL_ONLY_EVENT_TYPES.EVENT_MEDIA_WEBAPP_MQTT_ERROR:
            statusForCameraType = EVENT_MEDIA_STATUS.ERROR_MQTT_FAILED;
            errorFound = true;
            break;
        case "EVENT_MEDIA_UPLOAD_COMPLETED":
            statusForCameraType = EVENT_MEDIA_STATUS.MEDIA_UPLOAD_COMPLETED;
            break;
        default:
            if (eventUserMediaRequest.get('error')) {
                statusForCameraType = EVENT_MEDIA_STATUS.ERROR;
                errorFound = true;
                break;
            }
            statusForCameraType = EVENT_MEDIA_STATUS.CREATE_PREVIEW_VIDEO_REQUEST_SENT;
            break;
    }

    if (!errorFound) return statusForCameraType;

    const probableCameraTypeForError = getCameraTypeForEventUserMediaRequest(eventUserMediaRequest);
    if (probableCameraTypeForError === cameraType) return statusForCameraType;

    return null; // do not provide the error status if it is likely for the other cameraType
}

const getStatus = (
    mediaRequestType,
    cameraType,
    event,
    sourceMedia,
    previewImageMedia,
    previewVideoMedia
) => {
    const eventUserMediaRequest = event.get('userMediaRequest');

    if ((!previewImageMedia) || (!previewImageMedia.get("key"))) {
        if (mediaRequestType === EVENT_MEDIA_REQUEST_TYPE.NONE) {
            return EVENT_MEDIA_STATUS.NOT_FOUND_PREVIEW_IMAGE;
        }
        // Special case: else check if video may be available despite missing corresponding preview image
        if (!previewVideoMedia) {
            if (mediaRequestType === EVENT_MEDIA_REQUEST_TYPE.VIDEO_PREVIEW_REQUEST) { // DEPRECATED_VIDEO_PREVIEW_REQUEST
                if (sourceMedia) {
                    if (sourceMedia.get('requestInProgress')) {
                        return EVENT_MEDIA_STATUS.CREATE_PREVIEW_VIDEO_REQUEST_SENT;
                    }
                    if (sourceMedia.get('error')) {
                        return EVENT_MEDIA_STATUS.ERROR; // DEPRECATED_VIDEO_PREVIEW_REQUEST
                    }
                    if (sourceMedia.get('availableOnRaven')) {
                        return EVENT_MEDIA_STATUS.SOURCE_AVAILABLE_WITHOUT_PREVIEW_IMAGE;
                    }
                }
            }
            if (eventUserMediaRequest) {
                if (eventUserMediaRequest.get('request')) {
                    const statusForCameraType = getUserMediaRequestStatus(eventUserMediaRequest, cameraType);
                    if (statusForCameraType) return statusForCameraType;
                }
            } else if (getEventVideoAvailable(event, cameraType)) {
                return EVENT_MEDIA_STATUS.SOURCE_AVAILABLE_WITHOUT_PREVIEW_IMAGE;                    
            }
            return EVENT_MEDIA_STATUS.NOT_FOUND_PREVIEW_IMAGE;
        }
    }
    // If Raven doesn't support creating video previews, we can bail now
    if (mediaRequestType === EVENT_MEDIA_REQUEST_TYPE.NONE) {
        return EVENT_MEDIA_STATUS.SOURCE_GONE;
    }


    // This check catches both when we first request the media from Raven (until the preview video has its own record)
    // and when we retry (until the new preview video replacement has a media record)
    if (mediaRequestType === EVENT_MEDIA_REQUEST_TYPE.VIDEO_PREVIEW_REQUEST) { // DEPRECATED_VIDEO_PREVIEW_REQUEST
        if (sourceMedia && sourceMedia.get('requestInProgress')) {
            return EVENT_MEDIA_STATUS.CREATE_PREVIEW_VIDEO_REQUEST_SENT;
        }
    }
    if (eventUserMediaRequest && eventUserMediaRequest.get('request')) {  // Check if request was made/in-progress
        const statusForCameraType = getUserMediaRequestStatus(eventUserMediaRequest, cameraType);
        if (statusForCameraType) return statusForCameraType;
    }

    if (!previewVideoMedia) {
        if (mediaRequestType === EVENT_MEDIA_REQUEST_TYPE.VIDEO_PREVIEW_REQUEST) { // DEPRECATED_VIDEO_PREVIEW_REQUEST
            if (!sourceMedia || !sourceMedia.get('availableOnRaven')) {
                return EVENT_MEDIA_STATUS.SOURCE_GONE;
            }
            if (sourceMedia.get('error')) {
                return EVENT_MEDIA_STATUS.ERROR;
            }
            return EVENT_MEDIA_STATUS.SOURCE_AVAILABLE;
        }

        // check source video availability
        if (!getEventVideoAvailable(event, cameraType)) {
            if (sourceMedia && sourceMedia.get('availableOnRaven')) { // LEGACY PATCH USING DEPRECATED_VIDEO_PREVIEW_REQUEST 
                return EVENT_MEDIA_STATUS.SOURCE_AVAILABLE;
            }
            return EVENT_MEDIA_STATUS.SOURCE_GONE;
        }
        return EVENT_MEDIA_STATUS.SOURCE_AVAILABLE;
    }

    // If we have the signedUrl, it's all good unless there's a playback error
    if (previewVideoMedia.get('signedUrl')) {
        if (previewVideoMedia.get('playbackError')) {
            return EVENT_MEDIA_STATUS.ERROR_PLAYBACK_FAILED;
        }
        return EVENT_MEDIA_STATUS.PREVIEW_VIDEO_AVAILABLE;
    } else if (previewVideoMedia.get('key')) {
        return EVENT_MEDIA_STATUS.CREATE_SIGNED_URL_IN_PROGRESS;
    }
    // if USER_MEDIA_REQUEST, seems like an error occurred:
    if (mediaRequestType === EVENT_MEDIA_REQUEST_TYPE.USER_MEDIA_REQUEST) {
        if (previewVideoMedia.get('error')) {
            return EVENT_MEDIA_STATUS.ERROR;
        }
    }

    // DEPRECATED_VIDEO_PREVIEW_REQUEST (timeouts of this nature now handled in data/events/actions.js)
    var expiryStatus;
    switch(previewVideoMedia.get('status')) {
        case 'EVENT_MEDIA_FAILED':
        case 'EVENT_MEDIA_UPLOAD_FAILED':
            return EVENT_MEDIA_STATUS.ERROR;
        case 'EVENT_MEDIA_INITIATED':
            expiryStatus = expiryCheck(previewVideoMedia.get('statusTime'), 60);
            if (mediaRequestType === EVENT_MEDIA_REQUEST_TYPE.VIDEO_PREVIEW_REQUEST) { // DEPRECATED_VIDEO_PREVIEW_REQUEST
                return expiryStatus;
            }
            return expiryStatus === EVENT_MEDIA_STATUS.ERROR ? EVENT_MEDIA_STATUS.ERROR : EVENT_MEDIA_STATUS.MEDIA_INITIATED

        case 'EVENT_MEDIA_CREATED':
            expiryStatus = expiryCheck(previewVideoMedia.get('statusTime'), 60);
            if (mediaRequestType === EVENT_MEDIA_REQUEST_TYPE.VIDEO_PREVIEW_REQUEST) { // DEPRECATED_VIDEO_PREVIEW_REQUEST
                return expiryStatus;
            }
            return expiryStatus === EVENT_MEDIA_STATUS.ERROR ? EVENT_MEDIA_STATUS.ERROR : EVENT_MEDIA_STATUS.MEDIA_CREATED

        case 'EVENT_MEDIA_UPLOAD_INITIATED':
            expiryStatus = expiryCheck(previewVideoMedia.get('statusTime'), 60);
            if (mediaRequestType === EVENT_MEDIA_REQUEST_TYPE.VIDEO_PREVIEW_REQUEST) { // DEPRECATED_VIDEO_PREVIEW_REQUEST
                return expiryStatus;
            }
            return expiryStatus === EVENT_MEDIA_STATUS.ERROR ? EVENT_MEDIA_STATUS.ERROR : EVENT_MEDIA_STATUS.MEDIA_UPLOAD_INITIATED

        case 'EVENT_MEDIA_UPLOAD_COMPLETED':
            expiryStatus = expiryCheck(previewVideoMedia.get('statusTime'), 30);
            if (mediaRequestType === EVENT_MEDIA_REQUEST_TYPE.VIDEO_PREVIEW_REQUEST) { // DEPRECATED_VIDEO_PREVIEW_REQUEST
                return expiryStatus;
            }
            return expiryStatus === EVENT_MEDIA_STATUS.ERROR ? EVENT_MEDIA_STATUS.ERROR : EVENT_MEDIA_STATUS.MEDIA_UPLOAD_COMPLETED

        default:
            console.error('EventMedia previewVideoMedia has unexpected status', previewVideoMedia);
            return EVENT_MEDIA_STATUS.ERROR;
    }
}

const mapStateToProps = (state, ownProps) => {
    const event = getEvent(state, ownProps);
    if (!event) return { status: EVENT_MEDIA_STATUS.ERROR };

    // determine the current default/selected camera type
    let cameraType = state.scenes.dashboard.EventMedia.cameraType;
    // determine the default for the event (side note: override is cleared if the user toggles camera)
    cameraType = cameraTypeOverride.getCameraType(state.scenes.dashboard.EventMedia.cameraType, event);
    // if this component should show the alternate camera, change this cameraType to show alternate
    if (ownProps.shouldShowAlternateCameraType) {
        if (cameraType === EVENT_MEDIA_CAMERA_TYPE.CAMERA_ROAD) {
            cameraType = EVENT_MEDIA_CAMERA_TYPE.CAMERA_CABIN;
        } else {
            cameraType = EVENT_MEDIA_CAMERA_TYPE.CAMERA_ROAD;
        }
    }

    const mediaList = getMediaForEvent(state, ownProps.selectedRavenUuid, event);

    const sourceMedia = getSourceMedia(mediaList, cameraType);
    const previewImageMedia = getPreviewImageMedia(mediaList, cameraType);
    const previewImageUrl = getUrl(previewImageMedia);
    const previewVideoMedia = getPreviewVideoMedia(mediaList, cameraType, ownProps.clusterItemIndex);
    const previewVideoUrl = getUrl(previewVideoMedia);

    const eventMediaStatus = getStatus(
        ownProps.mediaRequestType,
        cameraType,
        event,
        sourceMedia,
        previewImageMedia,
        previewVideoMedia
    );

    //console.log("EventMedia status " + eventMediaStatus + ", for mediaRequestType " + ownProps.mediaRequestType);
    return {
        cameraType,
        eventId: event.get('eventId'),
        eventType: getEventType(event),
        historicalStreamingEnabled: ownProps.features ? ownProps.features.historical_streaming : false,  // not a scalable prop, but very obvious for now
        sourceMediaId: sourceMedia ? sourceMedia.get('mediaId') : '',
        date: event.get('timestamp'),
        status: eventMediaStatus,
        previewImageUrl,
        previewVideoUrl,
        previewVideoMediaId: previewVideoMedia && previewVideoMedia.get('mediaId'),
        previewVideoCameraType: previewVideoMedia && previewVideoMedia.get('cameraType'),
    };
}

const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        createEventUserVideo: (eventId, cameraTypeForComponent, legacySourceMediaId) => {

            let cameraType = cameraTypeForComponent;
            // check if component is showing the alternate camera
            // ASSUMPTION: the media request should always be for the non-alternate camera type
            if (ownProps.shouldShowAlternateCameraType) {
                if (cameraType === EVENT_MEDIA_CAMERA_TYPE.CAMERA_ROAD) {
                    cameraType = EVENT_MEDIA_CAMERA_TYPE.CAMERA_CABIN;
                } else {
                    cameraType = EVENT_MEDIA_CAMERA_TYPE.CAMERA_ROAD;
                }
            }
        
            switch (ownProps.mediaRequestType) {

                case EVENT_MEDIA_REQUEST_TYPE.VIDEO_PREVIEW_REQUEST: // DEPRECATED_VIDEO_PREVIEW_REQUEST
                    dispatch(createPreviewVideo(
                        ownProps.mqttService,
                        ownProps.selectedRavenUuid,
                        legacySourceMediaId
                    ));
                    return;

                case EVENT_MEDIA_REQUEST_TYPE.USER_MEDIA_REQUEST:
                    dispatch(createEventUserVideo(
                        ownProps.mqttService,
                        ownProps.selectedRavenUuid,
                        cameraType,
                        eventId
                    ));
                    return;

                case EVENT_MEDIA_REQUEST_TYPE.NONE:
                    console.warn("EventMedia createEventUserVideo with EVENT_MEDIA_REQUEST_TYPE NONE");
                    return;
                default:
                    console.warn("EventMedia createEventUserVideo with invalid EVENT_MEDIA_REQUEST_TYPE");
                    return;
            }
        },
        onVideoError: (mediaId, error) => dispatch(
            videoPlaybackError(ownProps.selectedRavenUuid, mediaId, error)
        ),
        toggleCamera: (cameraTypeForComponent) => {
            let currentCamera = cameraTypeForComponent;
            // check if component is showing the alternate camera
            // ASSUMPTION: the toggle request should always be for the non-alternate camera type
            if (ownProps.shouldShowAlternateCameraType) {
                if (currentCamera === EVENT_MEDIA_CAMERA_TYPE.CAMERA_ROAD) {
                    currentCamera = EVENT_MEDIA_CAMERA_TYPE.CAMERA_CABIN;
                } else {
                    currentCamera = EVENT_MEDIA_CAMERA_TYPE.CAMERA_ROAD;
                }
            }
            cameraTypeOverride.clearCameraTypeOverride(); // no longer force if the user has selected to toggle camera
            dispatch(setCamera(currentCamera === CameraType.CAMERA_ROAD ? CameraType.CAMERA_CABIN : CameraType.CAMERA_ROAD));
        },
        clearPlaybackError: (mediaId) => dispatch(
            clearPlaybackError(ownProps.selectedRavenUuid, mediaId)
        ),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(EventMedia);
